import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminLayout from '../components/AdminLayout'
import {
  ClipboardList, X, CheckCircle, AlertCircle, Loader,
  Search, ChevronLeft, ChevronRight, Calendar,
  MapPin, Truck, User, Weight, Clock, Eye, Check,
  XCircle, RefreshCw, Grid, Table, CalendarDays,
  AlertTriangle, ArrowLeftRight,
} from 'lucide-react'
import {
  FetchBookingRequests,
  AdminFindNearestDriver,
  AdminAcceptBooking,
  AdminCancelBooking,
  ResetAdminBookingRequest,
} from '../actions/AdminBookingRequestAction'

// ─── Helper Functions ────────────────────────────────────────────────────────
const parseBookingTime = (val) => {
  if (!val) return null
  const d = new Date(val)
  return isNaN(d.getTime()) ? null : d
}

const doTimesOverlap = (a, b) => {
  const aStart = parseBookingTime(a.bsTime)
  const aEnd   = parseBookingTime(a.beTime)
  const bStart = parseBookingTime(b.bsTime)
  const bEnd   = parseBookingTime(b.beTime)
  if (!aStart || !aEnd || !bStart || !bEnd) return false
  return aStart < bEnd && bStart < aEnd
}

const normalizeTonnage = (t) =>
  String(t || '').toLowerCase().replace(/[^0-9.]/g, '')

const getConflictingReqIds = (bookings) => {
  const conflicts = new Set()
  for (let i = 0; i < bookings.length; i++) {
    for (let j = i + 1; j < bookings.length; j++) {
      const a = bookings[i]
      const b = bookings[j]
      if (normalizeTonnage(a.tonnage) === normalizeTonnage(b.tonnage) && doTimesOverlap(a, b)) {
        conflicts.add(String(a.bId || a.reqId))
        conflicts.add(String(b.bId || b.reqId))
      }
    }
  }
  return conflicts
}

const fmtTime = (val) =>
  val ? new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'

// ─── Filter Components ───────────────────────────────────────────────────────
function FilterInput({ placeholder, value, onChange, icon: Icon }) {
  return (
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white`}
      />
      {value && (
        <X
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition"
          onClick={() => onChange('')}
        />
      )}
    </div>
  )
}

function FilterDate({ value, onChange }) {
  return (
    <div className="relative">
      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
      />
      {value && (
        <X
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition"
          onClick={() => onChange('')}
        />
      )}
    </div>
  )
}

// ─── Info Field (Modal) ───────────────────────────────────────────────────────
function InfoField({ label, value, badge }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      {badge ? (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge}`}>
          {value}
        </span>
      ) : (
        <p className="text-sm font-medium text-gray-900 break-words">{value || '-'}</p>
      )}
    </div>
  )
}

// ─── Swap Checkbox Cell ───────────────────────────────────────────────────────
function SwapCheckbox({ reqId, selectedForSwap, onToggle }) {
  const isSelected  = selectedForSwap.includes(reqId)
  const isDisabled  = selectedForSwap.length >= 2 && !isSelected
  return (
    <label className={`flex items-center justify-center w-8 h-8 rounded-lg border-2 cursor-pointer transition-all select-none ${
      isSelected
        ? 'bg-indigo-600 border-indigo-600'
        : isDisabled
        ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
        : 'bg-white border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
    }`}>
      <input
        type="checkbox"
        checked={isSelected}
        disabled={isDisabled}
        onChange={() => onToggle(reqId)}
        className="sr-only"
      />
      {isSelected && <Check size={14} className="text-white" strokeWidth={3} />}
    </label>
  )
}

// ─── Table Row ────────────────────────────────────────────────────────────────
function TableRow({
  booking, onView, statusColor, normalizeDate,
  conflictIds, acceptedReqIds, selectedForSwap, onToggleSwapSelect,
}) {
  const id          = String(booking.bId || booking.reqId)
  const hasConflict = conflictIds.has(id)
  const isBlocked   = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(id)
  const isSwapSel   = selectedForSwap.includes(id)

  return (
    <tr className={`hover:bg-gray-50 transition-colors ${
      isSwapSel   ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-300'
      : hasConflict ? (isBlocked ? 'bg-red-50/40' : 'bg-yellow-50/40')
      : ''
    }`}>
      {/* Swap checkbox */}
      <td className="py-3 px-3">
        <SwapCheckbox reqId={id} selectedForSwap={selectedForSwap} onToggle={onToggleSwapSelect} />
      </td>

      {/* Req ID + conflict badge */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
          {hasConflict && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${
              isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              <AlertTriangle size={10} />
              {isBlocked ? 'Blocked' : 'Conflict'}
            </span>
          )}
        </div>
      </td>

      <td className="py-3 px-4 text-sm text-gray-600 font-medium">{booking.bookingSno}</td>

      <td className="py-3 px-4">
        <span className="inline-flex px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-lg">
          {booking.tonnage || '—'} tons
        </span>
      </td>

      <td className="py-3 px-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin size={14} className="text-gray-400 shrink-0" />
          <span className="truncate max-w-[180px]">{booking.From || '—'} → {booking.TO || '—'}</span>
        </div>
      </td>

      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <Clock size={12} className="text-gray-400" />
          <span>{fmtTime(booking.bsTime)} → {fmtTime(booking.beTime)}</span>
        </div>
      </td>

      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${statusColor(booking.status)}`}>
          {booking.status === 'Pending'   && <Clock size={10} />}
          {booking.status === 'Active'    && <CheckCircle size={10} />}
          {booking.status === 'Completed' && <Check size={10} />}
          {booking.status === 'Cancel'    && <XCircle size={10} />}
          {booking.status}
        </span>
      </td>

      <td className="py-3 px-4 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1">
          <CalendarDays size={12} />{booking.BookingStatus || '-'}
        </span>
      </td>

      <td className="py-3 px-4 text-sm text-gray-500">{normalizeDate(booking.bookingDT)}</td>

      <td className="py-3 px-4">
        <button
          onClick={() => onView(booking)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        >
          <Eye size={12} /> View
        </button>
      </td>
    </tr>
  )
}

// ─── Booking Card (Mobile) ────────────────────────────────────────────────────
function BookingCard({
  booking, onView, statusColor, normalizeDate,
  conflictIds, acceptedReqIds, selectedForSwap, onToggleSwapSelect,
}) {
  const id          = String(booking.bId || booking.reqId)
  const hasConflict = conflictIds.has(id)
  const isBlocked   = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(id)
  const isSwapSel   = selectedForSwap.includes(id)

  return (
    <div className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all ${
      isSwapSel   ? 'border-indigo-400 bg-indigo-50/40 ring-2 ring-indigo-200'
      : hasConflict ? (isBlocked ? 'border-red-200 bg-red-50/30' : 'border-yellow-200 bg-yellow-50/30')
      : 'border-gray-100'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {/* Swap checkbox */}
            <SwapCheckbox reqId={id} selectedForSwap={selectedForSwap} onToggle={onToggleSwapSelect} />

            <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(booking.status)}`}>
              {booking.status === 'Pending'   && <Clock size={10} />}
              {booking.status === 'Active'    && <CheckCircle size={10} />}
              {booking.status === 'Completed' && <Check size={10} />}
              {booking.status === 'Cancel'    && <XCircle size={10} />}
              {booking.status}
            </span>
            {hasConflict && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                <AlertTriangle size={10} />
                {isBlocked ? 'Blocked' : 'Conflict'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <User size={12} />
            <span>{booking.bookingSno}</span>
            <span>•</span>
            <Calendar size={12} />
            <span>{normalizeDate(booking.bookingDT)}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div>
          <p className="text-gray-500 mb-0.5">Tonnage</p>
          <p className="font-semibold text-gray-900">{booking.tonnage || '—'} tons</p>
        </div>
        <div>
          <p className="text-gray-500 mb-0.5">Booking Type</p>
          <p className="font-semibold text-gray-900">{booking.BookingStatus || '-'}</p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Route</p>
          <p className="font-semibold text-gray-900 flex items-center gap-1">
            <MapPin size={12} className="text-gray-400" />
            <span className="truncate">{booking.From || '—'} → {booking.TO || '—'}</span>
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Time Slot</p>
          <p className="font-semibold text-gray-900">
            {fmtTime(booking.bsTime)} → {fmtTime(booking.beTime)}
          </p>
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100">
        <button
          onClick={() => onView(booking)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        >
          <Eye size={14} /> View Details
        </button>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminScheduledBooking() {
  const dispatch    = useDispatch()
  const { authKey } = useSelector((state) => state.auth)

  const { nearest, ranked, loading: findingNearest, error: findError }      = useSelector((s) => s.adminFindNearestDriver)
  const { loading: accepting, success: acceptSuccess, error: acceptError }  = useSelector((s) => s.adminAcceptBooking)

  // UI
  const [viewMode,        setViewMode]        = useState('table')
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [acceptModal,     setAcceptModal]     = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [acceptedReqIds,  setAcceptedReqIds]  = useState(new Set())

  // ─── Local main fork bookings state (isolated — does NOT affect AdminBooking Redux)
  const [mainForkBookings,    setMainForkBookings]    = useState([])
  const [loadingMainFork,     setLoadingMainFork]     = useState(false)
  const [mainForkError,       setMainForkError]       = useState(null)

  // Swap state
  const [selectedForSwap, setSelectedForSwap] = useState([])
  const [swapping,        setSwapping]        = useState(false)
  const [swapSuccess,     setSwapSuccess]     = useState(false)
  const [swapError,       setSwapError]       = useState(null)

  // Filters
  const [scheduledReqId,     setScheduledReqId]     = useState('')
  const [scheduledVehicleId, setScheduledVehicleId] = useState('')
  const [scheduledDriverNo,  setScheduledDriverNo]  = useState('')
  const [scheduledTonnage,   setScheduledTonnage]   = useState('')
  const [scheduledDate,      setScheduledDate]      = useState('')

  // Filtered + pagination
  const [scheduledFiltered,    setScheduledFiltered]    = useState([])
  const [scheduledCurrentPage, setScheduledCurrentPage] = useState(1)
  const rowsPerPage = 10

  // WebSocket
  const wsRef           = useRef(null)
  const lastWsFetchRef  = useRef(0)
  const wsFetchTimerRef = useRef(null)
  const BOOKING_WS_URL  = 'wss://aeroviewbackend.dockyardsoftware.com:8095/Booking'

  // Screen size → default view mode
  useEffect(() => {
    const handleResize = () => setViewMode(window.innerWidth < 768 ? 'card' : 'table')
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const normalizeDate = (dt) => {
    if (!dt) return ''
    const d = new Date(dt)
    return isNaN(d) ? '' : d.toISOString().split('T')[0]
  }

  const isScheduled = (b) => {
    const bs = (b.BookingStatus || '').toLowerCase()
    return bs.includes('shedule') || bs.includes('schedule') || bs === 's'
  }

  // Kept for accept/cancel flows on this page; table data is sourced from GetMainForkBookingDetails only.
  const fetchScheduledBookings = useCallback(async () => {
    try { await dispatch(FetchBookingRequests()) } catch (_) {}
  }, [dispatch])

  // ─── Fetch GetMainForkBookingDetails locally (ISOLATED from AdminBooking Redux)
  //     Used to load this page table and to pass bId to swap API
  const fetchMainForkForSwap = useCallback(async () => {
    if (!authKey) return
    setLoadingMainFork(true)
    setMainForkError(null)
    try {
      const res = await fetch(
        'https://aeroviewbackend.dockyardsoftware.com/BookingRegister/GetMainForkBookingDetails',
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': authKey } }
      )
      const data = await res.json()
      if (data.statusCode === 200 && data.resultSet) {
        // Only keep Shedule Booking entries — these are the accepted scheduled ones with bId
        const shedule = data.resultSet.filter(
          (b) => (b.BookingStatus || '').toLowerCase().includes('shedule') ||
                 (b.BookingStatus || '').toLowerCase().includes('schedule')
        )
        setMainForkBookings(shedule)
      } else {
        setMainForkBookings([])
        setMainForkError(data?.message || 'Failed to load scheduled bookings')
      }
    } catch (err) {
      console.error('[fetchMainForkForSwap]', err)
      setMainForkBookings([])
      setMainForkError(err?.message || 'Failed to load scheduled bookings')
    } finally {
      setLoadingMainFork(false)
    }
  }, [authKey])

  useEffect(() => {
    if (authKey) {
      fetchMainForkForSwap()
    }
  }, [authKey, fetchMainForkForSwap])

  // WebSocket
  useEffect(() => {
    if (!authKey) return
    if (wsRef.current) wsRef.current.close()
    wsRef.current = new WebSocket(`${BOOKING_WS_URL}?token=${encodeURIComponent(authKey)}`)
    wsRef.current.onopen    = () => console.log('✅ Booking WebSocket Connected')
    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (message.type || message.action || message.bId || message.bookingId) {
          const now = Date.now(); const minGap = 5000
          if (now - lastWsFetchRef.current >= minGap) {
            lastWsFetchRef.current = now
            fetchMainForkForSwap()
          } else if (!wsFetchTimerRef.current) {
            const delay = minGap - (now - lastWsFetchRef.current)
            wsFetchTimerRef.current = setTimeout(() => {
              lastWsFetchRef.current = Date.now()
              wsFetchTimerRef.current = null
              fetchMainForkForSwap()
            }, delay)
          }
        }
      } catch (_) {}
    }
    wsRef.current.onerror = (e) => console.error('WebSocket Error:', e)
    wsRef.current.onclose = ()  => console.log('WebSocket Disconnected')
    return () => {
      wsRef.current?.close(); wsRef.current = null
      if (wsFetchTimerRef.current) { clearTimeout(wsFetchTimerRef.current); wsFetchTimerRef.current = null }
    }
  }, [authKey, fetchMainForkForSwap])

  // Filter effect — data source is GetMainForkBookingDetails (local state)
  useEffect(() => {
    let r = Array.isArray(mainForkBookings)
      ? mainForkBookings.filter((b) => isScheduled(b))
      : []
    if (scheduledReqId.trim())     r = r.filter(b => String(b.reqId || b.bId || '').includes(scheduledReqId.trim()))
    if (scheduledVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(scheduledVehicleId.trim().toLowerCase()))
    if (scheduledDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(scheduledDriverNo.trim().toLowerCase()))
    if (scheduledTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(scheduledTonnage.trim().toLowerCase()))
    if (scheduledDate)             r = r.filter(b => normalizeDate(b.bookingDT) === scheduledDate)
    r.sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))
    setScheduledFiltered(r)
    setScheduledCurrentPage(1)
  }, [scheduledReqId, scheduledVehicleId, scheduledDriverNo, scheduledTonnage, scheduledDate, mainForkBookings])

  // Conflict detection
  const scheduledConflictIds = useMemo(() => {
    const pending = Array.isArray(mainForkBookings)
      ? mainForkBookings.filter(b => isScheduled(b))
      : []
    return getConflictingReqIds(pending)
  }, [mainForkBookings])

  // All pending scheduled (for swap preview — ignores filters/pagination)
  const allScheduledPending = useMemo(() =>
    Array.isArray(mainForkBookings)
      ? mainForkBookings.filter(b => isScheduled(b))
      : [],
  [mainForkBookings])

  const getSwapSelectionId = useCallback((booking) => String(booking?.bId || booking?.reqId || ''), [])

  const swapBookingDetails = selectedForSwap
    .map(id => allScheduledPending.find(b => getSwapSelectionId(b) === id))
    .filter(Boolean)

  useEffect(() => { if (nearest) setSelectedVehicle(nearest) }, [nearest])

  // ─── Resolve bId from mainForkBookings
  const resolveBId = (selectedId) => {
    const match = mainForkBookings.find(
      (b) => String(b.bId) === String(selectedId) || String(b.reqId) === String(selectedId)
    )
    if (match) {
      console.log(`[resolveBId] selected ${selectedId} → bId ${match.bId}`)
      return String(match.bId)
    }
    console.warn(`[resolveBId] No match found for ${selectedId}, falling back`)
    return String(selectedId)
  }

  // ─── Swap handlers
  const handleToggleSwapSelect = (reqId) => {
    setSelectedForSwap(prev => {
      if (prev.includes(reqId)) return prev.filter(id => id !== reqId)
      if (prev.length >= 2) return prev
      return [...prev, reqId]
    })
    setSwapSuccess(false)
    setSwapError(null)
  }

  const handleSwapTimes = async () => {
    if (selectedForSwap.length !== 2) return

    // Resolve bId for both selected bookings from GetMainForkBookingDetails
    const bId1 = resolveBId(selectedForSwap[0])
    const bId2 = resolveBId(selectedForSwap[1])

    console.log(`[handleSwapTimes] Swapping bId1=${bId1} with bId2=${bId2}`)

    setSwapping(true)
    setSwapError(null)
    setSwapSuccess(false)
    try {
      const res = await fetch(
        `https://aeroviewbackend.dockyardsoftware.com/BookingRegister/PostForkBookingForSwap?booking1=${encodeURIComponent(bId1)}&booking2=${encodeURIComponent(bId2)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', 'auth-key': authKey } }
      )
      if (!res.ok) throw new Error(`Server responded with ${res.status}`)
      setSwapSuccess(true)
      setSelectedForSwap([])
      // Refresh main fork data after swap
      await fetchMainForkForSwap()
    } catch (err) {
      setSwapError(err.message || 'Swap failed. Please try again.')
    } finally {
      setSwapping(false)
    }
  }

  // ─── Accept handlers
  const handleOpenAcceptModal = async (booking) => {
    dispatch(ResetAdminBookingRequest())
    setSelectedVehicle(null)
    setAcceptModal(booking)
    try { await dispatch(AdminFindNearestDriver(booking)) } catch (_) {}
  }

  const handleCancelBooking = async (reqId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return
    try { await dispatch(AdminCancelBooking(reqId)); await fetchScheduledBookings() }
    catch (err) { console.error(err); alert('❌ Cancel failed') }
  }

  const validateRequestInfo = async (reqId, key) => {
    try {
      const res = await fetch(
        `https://aeroviewbackend.dockyardsoftware.com/BookingRegister/Getrequestinfo?rid=${reqId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': key } }
      )
      return await res.json()
    } catch (err) { console.error('Validation API error:', err); return false }
  }

  const handleConfirmAccept = async () => {
    if (!acceptModal || !selectedVehicle) return
    try {
      await dispatch(AdminAcceptBooking(acceptModal, selectedVehicle, authKey))
      const reqId   = acceptModal.reqId || acceptModal.bId
      const isValid = await validateRequestInfo(reqId, authKey)
      if (!isValid) { alert('❌ Request validation failed.'); return }
      setAcceptedReqIds(prev => new Set([...prev, String(reqId)]))
      await Promise.all([fetchScheduledBookings(), fetchMainForkForSwap()])
      setAcceptModal(null)
      dispatch(ResetAdminBookingRequest())
    } catch (error) { console.error(error); alert('❌ Something went wrong') }
  }

  const handleCloseAcceptModal = () => {
    setAcceptModal(null); setSelectedVehicle(null); dispatch(ResetAdminBookingRequest())
  }

  const statusColor = (s) => {
    switch (s) {
      case 'Active':      return 'bg-emerald-50 text-emerald-700'
      case 'Pending':     return 'bg-yellow-50 text-yellow-700'
      case 'Completed':   return 'bg-blue-50 text-blue-700'
      case 'Enroute':     return 'bg-purple-50 text-purple-700'
      case 'In Progress': return 'bg-indigo-50 text-indigo-700'
      case 'Cancel':      return 'bg-red-50 text-red-700'
      default:            return 'bg-gray-50 text-gray-700'
    }
  }

  const totalPages = Math.max(1, Math.ceil(scheduledFiltered.length / rowsPerPage))
  const pageData   = scheduledFiltered.slice(
    (scheduledCurrentPage - 1) * rowsPerPage,
    scheduledCurrentPage * rowsPerPage
  )

  const hasActiveFilters = !!(scheduledReqId || scheduledVehicleId || scheduledDriverNo || scheduledTonnage || scheduledDate)

  return (
    <AdminLayout>
      {/* ─── Header ──────────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-xl">
                <CalendarDays size={22} className="text-indigo-600" />
              </div>
              Scheduled Bookings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage pending scheduled requests · select two bookings to swap their time slots
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-xs font-medium text-emerald-700">Live Updates Active</span>
            </div>
            <button
              onClick={fetchMainForkForSwap}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw size={16} className={loadingMainFork ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Swap success / error banners ────────────────────────────────────── */}
      {swapSuccess && (
        <div className="mb-5 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm font-medium shadow-sm">
          <CheckCircle size={18} className="text-emerald-600 shrink-0" />
          Time slots swapped successfully!
          <button onClick={() => setSwapSuccess(false)} className="ml-auto text-emerald-500 hover:text-emerald-700 p-1 rounded">
            <X size={14} />
          </button>
        </div>
      )}
      {swapError && (
        <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm font-medium shadow-sm">
          <AlertCircle size={18} className="text-red-500 shrink-0" />
          {swapError}
          <button onClick={() => setSwapError(null)} className="ml-auto text-red-400 hover:text-red-600 p-1 rounded">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ─── Legend ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-5 mb-5 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
          <span className="text-gray-600">Conflict — Overlapping time slot</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400"></div>
          <span className="text-gray-600">Blocked — Another overlapping booking accepted</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-indigo-200 border border-indigo-400"></div>
          <span className="text-gray-600">Selected for time swap</span>
        </div>
      </div>

      {/* ─── Filter bar ──────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <FilterInput placeholder="Request ID"  value={scheduledReqId}     onChange={setScheduledReqId}     icon={Search}  />
          <FilterInput placeholder="Vehicle ID"  value={scheduledVehicleId} onChange={setScheduledVehicleId} icon={Truck}   />
          <FilterInput placeholder="Driver No"   value={scheduledDriverNo}  onChange={setScheduledDriverNo}  icon={User}    />
          <FilterInput placeholder="Tonnage"     value={scheduledTonnage}   onChange={setScheduledTonnage}   icon={Weight}  />
          <FilterDate  value={scheduledDate}     onChange={setScheduledDate} />
        </div>
        {hasActiveFilters && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{scheduledFiltered.length}</span> filtered results
            </p>
            <button
              onClick={() => { setScheduledReqId(''); setScheduledVehicleId(''); setScheduledDriverNo(''); setScheduledTonnage(''); setScheduledDate('') }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              <X size={12} />Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* ─── View toggle + count ─────────────────────────────────────────────── */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">
          {scheduledFiltered.length} booking{scheduledFiltered.length !== 1 ? 's' : ''} found
          {selectedForSwap.length > 0 && (
            <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
              <ArrowLeftRight size={11} />
              {selectedForSwap.length === 1 ? '1 selected — pick 1 more' : '2 selected for swap'}
            </span>
          )}
        </p>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Table size={14} className="inline mr-1.5" />Table
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'card' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Grid size={14} className="inline mr-1.5" />Cards
          </button>
        </div>
      </div>

      {/* ─── Main table / cards ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loadingMainFork ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="text-gray-400 animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Loading bookings...</span>
          </div>
        ) : mainForkError ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
              <AlertCircle size={16} />{mainForkError}
            </div>
          </div>
        ) : scheduledFiltered.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-full">
                <ClipboardList size={32} className="text-gray-300" />
              </div>
              <p className="text-sm text-gray-500">No pending scheduled booking requests found</p>
            </div>
          </div>
        ) : (
          <>
            {/* Table view */}
            <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="py-3 px-3 w-12">
                      <span className="text-xs font-semibold text-indigo-500 uppercase tracking-wider">Swap</span>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Req ID</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Booked By</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time Slot</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageData.map((b) => (
                    <TableRow
                      key={b.bId || b.reqId}
                      booking={b}
                      onView={setSelectedBooking}
                      statusColor={statusColor}
                      normalizeDate={normalizeDate}
                      conflictIds={scheduledConflictIds}
                      acceptedReqIds={acceptedReqIds}
                      selectedForSwap={selectedForSwap}
                      onToggleSwapSelect={handleToggleSwapSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card view */}
            <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-4 space-y-3`}>
              {pageData.map((b) => (
                <BookingCard
                  key={b.bId || b.reqId}
                  booking={b}
                  onView={setSelectedBooking}
                  statusColor={statusColor}
                  normalizeDate={normalizeDate}
                  conflictIds={scheduledConflictIds}
                  acceptedReqIds={acceptedReqIds}
                  selectedForSwap={selectedForSwap}
                  onToggleSwapSelect={handleToggleSwapSelect}
                />
              ))}
            </div>

            {/* Pagination */}
            {scheduledFiltered.length > rowsPerPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <p className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-700">{(scheduledCurrentPage - 1) * rowsPerPage + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium text-gray-700">{Math.min(scheduledCurrentPage * rowsPerPage, scheduledFiltered.length)}</span>
                  {' '}of{' '}
                  <span className="font-medium text-gray-700">{scheduledFiltered.length}</span> results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setScheduledCurrentPage(p => Math.max(1, p - 1))}
                    disabled={scheduledCurrentPage === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft size={14} />Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm text-gray-600">
                    Page {scheduledCurrentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setScheduledCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={scheduledCurrentPage === totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next<ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Floating Swap Bar ───────────────────────────────────────────────── */}
      {selectedForSwap.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 pointer-events-none">

          {/* Preview strip — only when 2 selected */}
          {swapBookingDetails.length === 2 && (
            <div className="pointer-events-auto flex items-center gap-3 bg-white border border-indigo-200 rounded-2xl shadow-xl px-5 py-2.5 text-xs text-gray-700">
              <div className="flex flex-col items-center">
                <span className="font-bold text-indigo-700">#{swapBookingDetails[0].reqId || swapBookingDetails[0].bId}</span>
                <span className="text-gray-500 mt-0.5">
                  {fmtTime(swapBookingDetails[0].bsTime)} – {fmtTime(swapBookingDetails[0].beTime)}
                </span>
                {/* Show resolved bId for transparency */}
                <span className="text-indigo-400 text-[10px] mt-0.5">
                  bId: {resolveBId(selectedForSwap[0])}
                </span>
              </div>

              <div className="flex flex-col items-center gap-1 px-2">
                <ArrowLeftRight size={16} className="text-indigo-500" />
                <span className="text-indigo-400 font-medium">swap</span>
              </div>

              <div className="flex flex-col items-center">
                <span className="font-bold text-indigo-700">#{swapBookingDetails[1].reqId || swapBookingDetails[1].bId}</span>
                <span className="text-gray-500 mt-0.5">
                  {fmtTime(swapBookingDetails[1].bsTime)} – {fmtTime(swapBookingDetails[1].beTime)}
                </span>
                {/* Show resolved bId for transparency */}
                <span className="text-indigo-400 text-[10px] mt-0.5">
                  bId: {resolveBId(selectedForSwap[1])}
                </span>
              </div>
            </div>
          )}

          {/* Action bar */}
          <div className="pointer-events-auto flex items-center gap-3 bg-white border border-indigo-200 rounded-2xl shadow-2xl px-4 py-2.5">
            <div className="flex items-center gap-2 pl-1">
              <div className={`w-2 h-2 rounded-full ${selectedForSwap.length === 2 ? 'bg-indigo-500' : 'bg-yellow-400'}`}></div>
              <span className="text-sm text-gray-700 font-medium whitespace-nowrap">
                {selectedForSwap.length === 1 ? 'Select 1 more booking to swap' : '2 bookings ready to swap'}
              </span>
            </div>

            <button
              onClick={handleSwapTimes}
              disabled={selectedForSwap.length !== 2 || swapping || loadingMainFork}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed text-white font-semibold px-5 py-2 rounded-xl shadow transition-all text-sm"
            >
              {swapping ? (
                <><RefreshCw size={15} className="animate-spin" />Swapping...</>
              ) : loadingMainFork ? (
                <><RefreshCw size={15} className="animate-spin" />Loading...</>
              ) : (
                <><ArrowLeftRight size={15} />Swap Time Slots</>
              )}
            </button>

            <button
              onClick={() => { setSelectedForSwap([]); setSwapError(null) }}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Clear selection"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ─── Booking Detail Modal ────────────────────────────────────────────── */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-xl">
                  <CalendarDays size={18} className="text-indigo-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Booking Details</h2>
              </div>
              <button onClick={() => setSelectedBooking(null)} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoField label="Request ID"      value={selectedBooking.reqId || selectedBooking.bId} />
                <InfoField label="Status"          value={selectedBooking.status} badge={statusColor(selectedBooking.status)} />
                <InfoField label="Booked By"       value={selectedBooking.bookingSno} />
                <InfoField label="Tonnage"         value={selectedBooking.tonnage ? `${selectedBooking.tonnage} tons` : '-'} />
                <InfoField label="Vehicle ID"      value={selectedBooking.vId   || '-'} />
                <InfoField label="Driver No"       value={selectedBooking.dSNo  || '-'} />
                <InfoField label="Booking Type"    value={selectedBooking.BookingStatus || '-'} />
                <InfoField label="Booking Date"    value={normalizeDate(selectedBooking.bookingDT)} />
                <InfoField label="From"            value={selectedBooking.From  || '-'} />
                <InfoField label="To"              value={selectedBooking.TO    || '-'} />
                <InfoField label="Start Location"  value={`${selectedBooking.slatitude || '-'}, ${selectedBooking.slongitude || '-'}`} />
                <InfoField label="End Location"    value={`${selectedBooking.elatitude || '-'}, ${selectedBooking.elongitude || '-'}`} />
                <InfoField label="Scheduled Start" value={selectedBooking.bsTime ? new Date(selectedBooking.bsTime).toLocaleString() : '-'} />
                <InfoField label="Scheduled End"   value={selectedBooking.beTime ? new Date(selectedBooking.beTime).toLocaleString() : '-'} />
              </div>
              <div className="mt-3">
                <InfoField label="Remark" value={selectedBooking.Remark || '-'} />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                onClick={() => setSelectedBooking(null)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Accept & Assign Modal ───────────────────────────────────────────── */}
      {acceptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <CheckCircle size={18} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Accept & Assign Booking</h2>
              </div>
              <button onClick={handleCloseAcceptModal} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <ClipboardList size={14} />Booking Summary
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Request ID:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.reqId || acceptModal.bId}</p></div>
                  {/* <div><span className="text-gray-500">Tonnage:</span><p className="font-semibold text-orange-600 mt-0.5">{acceptModal.tonnage} tons</p></div> */}
                  <div><span className="text-gray-500">From:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.From || '—'}</p></div>
                  <div><span className="text-gray-500">To:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.TO || '—'}</p></div>
                  <div><span className="text-gray-500">Booked By:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bookingSno}</p></div>
                  <div><span className="text-gray-500">Sched. Start:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bsTime ? new Date(acceptModal.bsTime).toLocaleString() : '-'}</p></div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Truck size={14} />Select Vehicle to Assign
                </h3>
                {findingNearest && (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                    <RefreshCw size={18} className="text-blue-600 animate-spin" />
                    <span className="text-sm text-blue-700">Finding matching vehicles...</span>
                  </div>
                )}
                {findError && !findingNearest && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
                    <AlertCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">No Matching Vehicle Found</p>
                      <p className="text-xs text-red-600 mt-1">{findError}</p>
                    </div>
                  </div>
                )}
                {ranked.length > 0 && !findingNearest && (
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                    {ranked.map((v, idx) => {
                      const isSelected = selectedVehicle?.vehicleId === v.vehicleId
                      const isNearest  = idx === 0
                      const isDisabled = !isNearest
                      return (
                        <div
                          key={v.vehicleId}
                          onClick={() => isNearest && setSelectedVehicle(v)}
                          className={`rounded-xl border-2 p-4 transition-all ${
                            isDisabled
                              ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200'
                              : isSelected
                              ? 'cursor-pointer border-emerald-500 bg-emerald-50'
                              : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {isSelected
                                ? <CheckCircle size={18} className="text-emerald-600" />
                                : <div className={`w-4 h-4 rounded-full border-2 ${isDisabled ? 'border-gray-200' : 'border-gray-300'}`} />
                              }
                              <span className={`text-base font-semibold ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{v.vehicleId}</span>
                              {isNearest && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Nearest ✓ Only selectable</span>}
                              {isDisabled && <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Not available</span>}
                            </div>
                            <span className={`text-sm font-semibold ${isDisabled ? 'text-gray-400' : 'text-blue-600'}`}>{v.distanceKm} km</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm ml-7">
                            <div><span className="text-gray-400">Driver:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>{v.driverSno}</span></div>
                            <div><span className="text-gray-400">Weight:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : 'text-orange-600'}`}>{v.weightCat}</span></div>
                            <div><span className="text-gray-400">Type:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>{v.vehicleType}</span></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {acceptError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600 flex items-center gap-2"><AlertCircle size={14} />{acceptError}</p>
                </div>
              )}
              {acceptSuccess && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <p className="text-sm text-emerald-600 flex items-center gap-2"><CheckCircle size={14} />Booking accepted — request sent to driver successfully.</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button onClick={handleCloseAcceptModal} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition">
                Cancel
              </button>
              <button
                onClick={handleConfirmAccept}
                disabled={!selectedVehicle || findingNearest || accepting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {accepting
                  ? <><RefreshCw size={16} className="animate-spin" />Sending to Driver...</>
                  : <><CheckCircle size={16} />Accept & Assign {selectedVehicle ? `(${selectedVehicle.vehicleId})` : ''}</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
